import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureException } from './sentry';

export interface AppErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback UI */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Catches React render errors, reports them, and shows a minimal recovery UI.
 */
export class AppErrorBoundary extends Component<AppErrorBoundaryProps, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    captureException(error, {
      source: 'react.error_boundary',
      componentStack: info.componentStack,
    });
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            padding: 24,
            fontFamily: 'system-ui, sans-serif',
            color: '#1a1a1a',
            background: '#f5f5f5',
          }}
        >
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Something went wrong</h1>
          <p style={{ margin: 0, fontSize: 14, color: '#555', textAlign: 'center', maxWidth: 360 }}>
            An unexpected error occurred. Please reload the page. If the problem continues, contact
            support.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              marginTop: 8,
              padding: '8px 16px',
              fontSize: 14,
              cursor: 'pointer',
              border: '1px solid #ccc',
              borderRadius: 4,
              background: '#fff',
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
